import {
  AuthenticationResult,
  IPublicClientApplication,
  createNestablePublicClientApplication,
  InteractionRequiredAuthError,
  ServerError,
  BrowserAuthError,
} from '@azure/msal-browser';
import {
  AdminConsentRequiredError,
  isAdminConsentRequiredError,
  logInfo,
  PopupAuthError,
  TokenAcquisitionError,
} from '@utils-global';

const SILENT_TOKEN_TIMEOUT_MS = 5_000;

type TokenResult = { accessToken: string; idToken: string };

const isInteractionRequired = (error: unknown): boolean =>
  error instanceof InteractionRequiredAuthError || error instanceof BrowserAuthError;

const isPopupError = (error: unknown): boolean => {
  const errorCode = (error as any)?.errorCode;
  const errorStr = JSON.stringify(error);

  return (
    errorCode === 'popup_window_error' ||
    errorCode === 'user_cancelled' ||
    errorStr.includes('popup_window_error') ||
    errorStr.includes('popup_blocked')
  );
};

const toTokenResult = (result: AuthenticationResult): TokenResult => ({
  accessToken: result.accessToken,
  idToken: result.idToken,
});

export class OfficeAccountManager {
  private pca: IPublicClientApplication;

  async initialize() {
    this.pca = await createNestablePublicClientApplication({
      auth: {
        clientId: process.env.AZURE_CLIENT_ID,
        authority: 'https://login.microsoftonline.com/common',
      },
      cache: { cacheLocation: 'localStorage' },
    });
  }

  async ssoGetUserIdentity(
    scopes: string[],
    acquireInteractively: boolean,
    claims?: string,
  ): Promise<TokenResult | null> {
    if (!this.pca) {
      throw new Error('OfficeAccountManager is not initialized!');
    }

    const activeAccount = this.pca.getActiveAccount();

    // No active account - go straight to interactive (if allowed)
    if (!activeAccount) {
      if (!acquireInteractively) {
        return null;
      }
      logInfo('No active account, acquiring token interactively');
      return this.acquireTokenInteractively(scopes, claims);
    }

    // Try silent auth
    try {
      const result = await Promise.race([
        this.pca.acquireTokenSilent({
          scopes,
          account: activeAccount,
          ...(claims && { claims }),
        }),
        new Promise<never>((_, reject) => {
          setTimeout(
            () => reject(new TokenAcquisitionError('Token acquisition timed out')),
            SILENT_TOKEN_TIMEOUT_MS,
          );
        }),
      ]);
      return toTokenResult(result);
    } catch (error) {
      if (isInteractionRequired(error)) {
        if (!acquireInteractively) {
          logInfo('Silent auth failed, interactive auth disabled', { error });
          return null;
        }
        logInfo('Silent auth failed, falling back to interactive', { error });
        return this.acquireTokenInteractively(scopes, claims);
      }

      // Check for admin consent error
      if (isAdminConsentRequiredError(error)) {
        logInfo('AdminConsentRequiredError during silent auth', { error });
        throw new AdminConsentRequiredError();
      }

      // Re-throw other errors to be handled by caller
      throw error;
    }
  }

  private async acquireTokenInteractively(scopes: string[], claims?: string): Promise<TokenResult> {
    const needsAccountSelection = !this.pca.getActiveAccount();

    try {
      const result = await this.pca.acquireTokenPopup({
        scopes,
        ...(needsAccountSelection && { prompt: 'select_account' }),
        ...(claims && { claims }),
      });

      if (needsAccountSelection) {
        this.pca.setActiveAccount(result.account);
      }

      return toTokenResult(result);
    } catch (error) {
      // Check for popup-specific errors (user cancelled or popup blocked)
      if (isPopupError(error)) {
        const errorCode = (error as any)?.errorCode;
        logInfo('Popup authentication failed - user should use login button', {
          errorCode,
          error,
        });
        throw new PopupAuthError(errorCode || 'popup_failed');
      }

      // Check for admin consent error
      if (isAdminConsentRequiredError(error)) {
        logInfo('AdminConsentRequiredError during interactive auth', { error });
        throw new AdminConsentRequiredError();
      }

      // Re-throw other errors to be handled by caller
      throw error;
    }
  }
}